<script setup lang="ts">
import { useHead } from "@unhead/vue"
import { useUrlSearchParams } from "@vueuse/core"
import { computed } from "vue"
import { EventDetailsForm } from "@/components"
import EventInformationList from "@/components/EventInformationList/EventInformationList.vue"
import { TabControl } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useConvertedRouteParam, useTranslation } from "@/composables"
import { ValidationException } from "@/http"
import { SidebarHostEventLayout } from "@/layouts"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const eventVaultId = useConvertedRouteParam("id", "/event/[id]/edit", value => Number(value))

const { t } = useTranslation()
const toast = useToast()

useHead({ title: t("event_edit_page.page_title") })

const urlParams = useUrlSearchParams<{ tab?: unknown }>()

const validTabs = ["details", "information"] as const
type TabId = typeof validTabs[number]

const activeTab = computed<TabId>({
  get() {
    const tab = urlParams.tab
    return typeof tab === "string" && (validTabs as readonly string[]).includes(tab) ? tab as TabId : "details"
  },
  set(value) {
    urlParams.tab = value
  },
})

const tabs = [
  { id: "details", label: t("event_edit_page.tab_details") },
  { id: "information", label: t("event_edit_page.tab_information") },
]

function onUpdateSuccess() {
  toast.success({ title: t("event_edit_page.success_title"), description: t("event_edit_page.success_content") })
}

function onUpdateError(err: unknown) {
  if (err instanceof ValidationException) {
    toast.error({ title: t("event_edit_page.validation_error_title"), description: t("event_edit_page.validation_error_content") })
  }
  else {
    toast.error()
  }
}
</script>

<template>
  <SidebarHostEventLayout>
    <h1 class="font-heading font-bold text-2xl mb-8">
      {{ t("event_edit_page.page_title") }}
    </h1>
    <TabControl
      v-model="activeTab"
      :tabs="tabs"
      :tablist-aria-label="t('event_edit_page.page_title')"
      class="mb-8"
    />
    <div v-if="activeTab === 'details'">
      <EventDetailsForm
        :event-vault-id="eventVaultId"
        @success="onUpdateSuccess"
        @error="onUpdateError"
      />
    </div>
    <div v-else-if="activeTab === 'information'">
      <EventInformationList v-if="eventVaultId" :event-vault-id="eventVaultId" />
    </div>
  </SidebarHostEventLayout>
</template>
